/**
 * Home Screen Route
 *
 * The main dashboard for Curie Compass displaying:
 * - Portfolio summary with total value and returns
 * - Portfolio health visualization
 * - Holdings list with performance indicators
 * - Active recommendations for action
 */

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";
import {
  PortfolioSummaryCard,
  PortfolioSummaryCardSkeleton,
  PortfolioHealthCard,
  PortfolioHealthCardSkeleton,
} from "@/components/portfolio";
import {
  HoldingCard,
  HoldingCardSkeleton,
  type HoldingCardData,
} from "@/components/holdings";
import {
  RecommendationPreviewCard,
  RecommendationPreviewCardSkeleton,
  type RecommendationPreview,
} from "@/components/recommendations";
import {
  useHomeData,
  useHoldings,
  useMultiQueryRefresh,
  useMultiQueryFreshness,
  getErrorMessage,
  getHoldingsErrorMessage,
} from "@/hooks";
import { StaleDataIndicator } from "@/components/ui/stale-data-indicator";
import type { HealthHolding } from "@/components/portfolio";
import type { EnrichedHolding } from "@/hooks";
import { RefreshCw, AlertCircle, Briefcase, Lightbulb, ChevronRight } from "lucide-react";
import * as React from "react";
import { GlobalErrorFallback } from "@/components/error";

export const Route = createFileRoute("/")({
  component: HomePage,
  errorComponent: GlobalErrorFallback,
});

// ============================================================================
// Data Transformation Helpers
// ============================================================================

/**
 * Transform enriched holding to HoldingCardData
 */
function transformToHoldingCardData(
  holding: EnrichedHolding,
  totalPortfolioValue: number
): HoldingCardData | null {
  if (!holding.fund) return null;

  const allocationPercent =
    totalPortfolioValue > 0
      ? (holding.currentValue / totalPortfolioValue) * 100
      : 0;

  return {
    id: holding.id,
    fundName: holding.fund.name,
    category: holding.fund.category,
    amcName: holding.fund.amcName,
    currentValue: holding.currentValue,
    totalReturns: holding.totalReturns,
    totalReturnsPercent: holding.totalReturnsPercent,
    returns1Y: holding.fund.returns1Y,
    categoryAverageReturns: undefined, // Not available in current API
    rank: holding.fund.rank,
    totalFundsInCategory: holding.fund.totalFundsInCategory,
    rating: holding.fund.rating,
    // consistencyScore is only available in holding detail view, not in list view
    allocationPercent,
  };
}

/**
 * Transform enriched holding to HealthHolding for PortfolioHealthCard
 */
function transformToHealthHolding(holding: EnrichedHolding): HealthHolding | null {
  if (!holding.fund) return null;

  return {
    id: holding.id,
    name: holding.fund.name,
    status: holding.fund.rating,
    value: holding.currentValue,
  };
}

/**
 * Transform home recommendation to RecommendationPreview
 */
function transformToRecommendationPreview(
  rec: { id: string; type: string; reason: string; impactDescription: string; priority: string },
  fundName: string
): RecommendationPreview {
  return {
    id: rec.id,
    type: rec.type as RecommendationPreview["type"],
    fundName,
    reason: rec.reason,
    suggestedAction: rec.impactDescription,
    priority: rec.priority as RecommendationPreview["priority"],
  };
}

// ============================================================================
// Section Components
// ============================================================================

interface SectionHeaderProps {
  title: string;
  icon: React.ReactNode;
  action?: {
    label: string;
    to: string;
  };
}

function SectionHeader({ title, icon, action }: SectionHeaderProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      </div>
      {action && (
        <Link
          to={action.to}
          className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          {action.label}
          <ChevronRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

// ============================================================================
// Empty State Components
// ============================================================================

function EmptyHoldings() {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-8 text-center">
      <Briefcase className="mx-auto h-12 w-12 text-muted-foreground" />
      <h3 className="mt-4 text-lg font-semibold text-foreground">
        No holdings yet
      </h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Start building your portfolio by adding your first mutual fund holding.
      </p>
      <Button asChild className="mt-4">
        <Link to="/explore">Explore Funds</Link>
      </Button>
    </div>
  );
}

function EmptyRecommendations() {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
      <Lightbulb className="mx-auto h-10 w-10 text-muted-foreground" />
      <h3 className="mt-3 text-base font-semibold text-foreground">
        No recommendations
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Your portfolio looks great! Check back later for new insights.
      </p>
    </div>
  );
}

// ============================================================================
// Error Component
// ============================================================================

interface ErrorDisplayProps {
  title: string;
  message: string;
  onRetry: () => void;
  isRetrying?: boolean;
}

function ErrorDisplay({ title, message, onRetry, isRetrying }: ErrorDisplayProps) {
  return (
    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
      <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
      <h3 className="mt-4 text-lg font-semibold text-destructive">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
      <Button
        variant="outline"
        onClick={onRetry}
        disabled={isRetrying}
        className="mt-4"
      >
        {isRetrying ? (
          <>
            <RefreshCw className="h-4 w-4 animate-spin" />
            Retrying...
          </>
        ) : (
          <>
            <RefreshCw className="h-4 w-4" />
            Try Again
          </>
        )}
      </Button>
    </div>
  );
}

// ============================================================================
// Skeleton Components
// ============================================================================

function HomePageSkeleton() {
  return (
    <div className="space-y-8">
      {/* Portfolio Summary Skeleton */}
      <PortfolioSummaryCardSkeleton />

      {/* Portfolio Health Skeleton */}
      <PortfolioHealthCardSkeleton />

      {/* Holdings Section Skeleton */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-muted-foreground" />
          <div className="h-6 w-24 animate-pulse rounded bg-muted" />
        </div>
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <HoldingCardSkeleton key={i} />
          ))}
        </div>
      </section>

      {/* Recommendations Section Skeleton */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-muted-foreground" />
          <div className="h-6 w-36 animate-pulse rounded bg-muted" />
        </div>
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => (
            <RecommendationPreviewCardSkeleton key={i} />
          ))}
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function HomePage() {
  const navigate = useNavigate();

  // Fetch home data (portfolio summary, recommendations, etc.)
  const {
    data: homeData,
    isLoading: isHomeLoading,
    isError: isHomeError,
    error: homeError,
    refetch: refetchHome,
    isFetching: isHomeFetching,
    dataUpdatedAt: homeDataUpdatedAt,
  } = useHomeData();

  // Fetch holdings list
  const {
    data: holdingsData,
    isLoading: isHoldingsLoading,
    isError: isHoldingsError,
    error: holdingsError,
    refetch: refetchHoldings,
    isFetching: isHoldingsFetching,
    dataUpdatedAt: holdingsDataUpdatedAt,
  } = useHoldings();

  // Combined loading state
  const isLoading = isHomeLoading || isHoldingsLoading;
  const isFetching = isHomeFetching || isHoldingsFetching;

  // Track data freshness across multiple queries
  const { isOffline, formattedLastUpdated, shouldShowWarning, dataUpdatedAt } =
    useMultiQueryFreshness([
      { dataUpdatedAt: homeDataUpdatedAt, isFetching: isHomeFetching },
      { dataUpdatedAt: holdingsDataUpdatedAt, isFetching: isHoldingsFetching },
    ]);

  // Pull to refresh using the multi-query hook
  const { isRefreshing, handleRefresh } = useMultiQueryRefresh({
    refetchFunctions: [refetchHome, refetchHoldings],
    isFetching,
  });

  // Navigate to holding detail
  const handleHoldingClick = useCallback(
    (holdingId: string) => {
      navigate({ to: "/holdings/$holdingId", params: { holdingId } });
    },
    [navigate]
  );

  // Show skeleton while loading
  if (isLoading) {
    return (
      <main className="container mx-auto max-w-3xl px-4 py-6">
        <HomePageSkeleton />
      </main>
    );
  }

  // Show error state if both queries failed
  if (isHomeError && isHoldingsError) {
    return (
      <main className="container mx-auto max-w-3xl px-4 py-6">
        <ErrorDisplay
          title="Failed to load data"
          message={getErrorMessage(homeError)}
          onRetry={handleRefresh}
          isRetrying={isFetching}
        />
      </main>
    );
  }

  // Transform holdings data for components
  const holdings = holdingsData?.holdings ?? [];
  // Compute totalPortfolioValue from holdings to ensure consistency with displayed values
  const totalPortfolioValue = holdings.reduce((sum, h) => sum + h.currentValue, 0);

  const holdingCards: HoldingCardData[] = holdings
    .map((h) => transformToHoldingCardData(h, totalPortfolioValue))
    .filter((h): h is HoldingCardData => h !== null);

  const healthHoldings: HealthHolding[] = holdings
    .map(transformToHealthHolding)
    .filter((h): h is HealthHolding => h !== null);

  // Transform recommendations
  const recommendations: RecommendationPreview[] = (
    homeData?.recommendations ?? []
  ).map((rec) => {
    // Try to find fund name from holdings
    const fundName = "Mutual Fund"; // Placeholder - API should provide this
    return transformToRecommendationPreview(rec, fundName);
  });

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
      className="h-full"
    >
      <main className="container mx-auto max-w-3xl px-4 py-6">
        {/* Header with Refresh Button and Stale Data Indicator */}
        <div className="mb-6 space-y-2">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing || isFetching || isOffline}
              className="gap-2"
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefreshing || isFetching ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">
                {isRefreshing || isFetching ? "Refreshing..." : "Refresh"}
              </span>
            </Button>
          </div>
          {/* Stale data indicator - shows when data was last updated */}
          {dataUpdatedAt && (
            <StaleDataIndicator
              dataUpdatedAt={dataUpdatedAt}
              isRefreshing={isRefreshing || isFetching}
              onRefresh={handleRefresh}
              isOffline={isOffline}
              showRefreshButton={false}
            />
          )}
        </div>

        <div className="space-y-8">
          {/* Portfolio Summary Section */}
          {homeData ? (
            <PortfolioSummaryCard data={homeData.portfolio} />
          ) : (
            <PortfolioSummaryCardSkeleton />
          )}

          {/* Portfolio Health Section */}
          {isHoldingsError ? (
            <ErrorDisplay
              title="Failed to load holdings"
              message={getHoldingsErrorMessage(holdingsError)}
              onRetry={() => refetchHoldings()}
              isRetrying={isHoldingsFetching}
            />
          ) : healthHoldings.length > 0 ? (
            <PortfolioHealthCard
              holdings={healthHoldings}
              totalValue={totalPortfolioValue}
            />
          ) : null}

          {/* Holdings Section */}
          <section className="space-y-4">
            <SectionHeader
              title="Holdings"
              icon={<Briefcase className="h-5 w-5 text-muted-foreground" />}
              action={
                holdingCards.length > 0
                  ? { label: "View All", to: "/holdings" }
                  : undefined
              }
            />

            {isHoldingsError ? (
              <ErrorDisplay
                title="Failed to load holdings"
                message={getHoldingsErrorMessage(holdingsError)}
                onRetry={() => refetchHoldings()}
                isRetrying={isHoldingsFetching}
              />
            ) : holdingCards.length === 0 ? (
              <EmptyHoldings />
            ) : (
              <div className="space-y-3">
                {holdingCards.map((holding) => (
                  <HoldingCard
                    key={holding.id}
                    data={holding}
                    onClick={handleHoldingClick}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Recommendations Section */}
          <section className="space-y-4">
            <SectionHeader
              title="Recommendations"
              icon={<Lightbulb className="h-5 w-5 text-muted-foreground" />}
              action={
                recommendations.length > 0
                  ? { label: "View All", to: "/recommendations" }
                  : undefined
              }
            />

            {isHomeError ? (
              <ErrorDisplay
                title="Failed to load recommendations"
                message={getErrorMessage(homeError)}
                onRetry={() => refetchHome()}
                isRetrying={isHomeFetching}
              />
            ) : recommendations.length === 0 ? (
              <EmptyRecommendations />
            ) : (
              <div className="space-y-3">
                {recommendations.map((rec) => (
                  <RecommendationPreviewCard
                    key={rec.id}
                    recommendation={rec}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </PullToRefresh>
  );
}
